import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import seoService from '../services/seo.service';

/**
 * Хук для автоматической загрузки и установки SEO мета-тегов
 * @param {string} pageType - Тип страницы (home, products, suppliers и т.д.)
 * @param {number} entityId - ID сущности (если применимо)
 * @param {object} customMeta - Пользовательские мета-теги (переопределяют загруженные)
 */
const useSEO = (pageType = null, entityId = null, customMeta = null) => {
  const location = useLocation();
  const customMetaRef = useRef(customMeta);
  customMetaRef.current = customMeta;

  // ✅ КРИТИЧНО: customMeta часто передаётся как inline object.
  // Если класть его напрямую в deps — будет бесконечный цикл (новый объект на каждый render).
  // Поэтому делаем стабильный ключ по содержимому.
  const customMetaKey = useMemo(() => {
    try {
      if (!customMeta || typeof customMeta !== 'object') return '';
      return JSON.stringify(customMeta);
    } catch (_) {
      return String(Date.now()); // fallback: пусть лучше обновится, чем зациклится
    }
  }, [customMeta]);

  useEffect(() => {
    const currentCustomMeta = customMetaRef.current;

    // Функция для определения типа страницы из URL если не указан явно
    const getPageTypeFromUrl = (pathname) => {
      // Если путь начинается с /app - это приложение
      if (pathname.startsWith('/app')) {
        return 'app'; // Используем общий тип для приложения
      }
      
      // Для публичных страниц
      const pageTypeMap = {
        '/': 'landing',
        '/about': 'about',
        '/pricing': 'pricing',
        '/contact': 'contact',
        '/company': 'company',
        '/login': 'app',
        '/register': 'app'
      };
      
      return pageTypeMap[pathname] || 'landing';
    };

    // ✅ Предотвращаем множественные одновременные запросы
    let cancelled = false;

    const loadMeta = async () => {
      try {
        // Определяем тип страницы
        const type = pageType || getPageTypeFromUrl(location.pathname);
        
        // Загружаем мета-теги с сервера (с кэшированием)
        const response = await seoService.getMetaTags(type, entityId);
        
        // Проверяем, не был ли компонент размонтирован
        if (cancelled) return;
        
        if (response.success && response.data) {
          // Объединяем загруженные теги с пользовательскими
          const metaData = {
            ...response.data,
            ...(currentCustomMeta && typeof currentCustomMeta === 'object' ? currentCustomMeta : {})
          };
          
          // Устанавливаем мета-теги
          seoService.setMetaTags(metaData);
        }
      } catch (error) {
        // При ошибке устанавливаем хотя бы пользовательские теги
        if (
          !cancelled &&
          currentCustomMeta &&
          typeof currentCustomMeta === 'object' &&
          Object.keys(currentCustomMeta).length > 0
        ) {
          seoService.setMetaTags(currentCustomMeta);
        }
      }
    };

    loadMeta();
    
    return () => {
      cancelled = true;
    };
  }, [pageType, entityId, location.pathname, customMetaKey]);
};

export default useSEO;

